import {
  Pagination,
  withErrorBoundary,
  Loader,
  NoDataCard,
  ApiProgressLoader,
  EventEmitter
} from '@nile/components';
import envConfig from '@nile/home/src/Config/env';
import { FormattedMessage } from 'react-intl';
import { useVirtual } from 'react-virtual';
import ActionCell from '@nile/banyan/src/Pages/ServiceCatalog/ActionCell';
import LinkCell from '@nile/banyan/src/Pages/ServiceCatalog/LinkCell';
import { TextCell } from '@nile/mangifera/src/Components/Ticket';
import { SVGIcon } from '@superops/super-icons';
import { Button } from '@superops/ui-design-system';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { loggerInfo } from '@nile/home/src/coralogixRUM';
import DataCell from './DataCell';
import TableHeader from './TableHeader';
import { getAttributeName } from './TableHelpers';
import Styles, {
  SelectAllBannerStyles,
  RefreshButtonStyles
} from './TableStyles';
import { cloneDeep, isEmpty, isEqual } from 'lodash';

const paddedValuesForPagination = {
  sm: 'ml-10',
  lg: 'ml-30'
};

const SELECTION_MODE = {
  ALL_SELECT: 'ALL_SELECT'
};

const LoadingState = () => {
  return (
    <>
      <div className="loading-state">
        <Loader />
      </div>
    </>
  );
};

export function IndeterminateCheckbox({
  indeterminate,
  className,
  idKey,
  ...rest
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  const checkBoxDisabled =
    rest?.checkBoxDisabledCheck &&
    rest?.checkBoxDisabledCheck(rest?.row?.original);

  const value = rest?.row?.original?.[idKey] || '';

  return (
    <input
      type="checkbox"
      ref={ref}
      className={`${className || ''} cp`}
      disabled={checkBoxDisabled}
      value={value}
      {...rest}
    />
  );
}

const SelectAllBanner = (props) => {
  const {
    selectAllBannerStyles,
    selectionMode,
    onSelectAll,
    records,
    clearSelection,
    totalCount
  } = props;

  return (
    <SelectAllBannerStyles styles={selectAllBannerStyles || {}}>
      <div className="svg-icon-wrapper">
        <SVGIcon name="About" />
      </div>

      <p className="infoMessage">
        {selectionMode === SELECTION_MODE.ALL_SELECT ? (
          // <>ALL {records?.totalRecords} are selected</>
          <>
            <FormattedMessage
              id="superTable.selectAll.message"
              values={{
                totalCount: totalCount,
                selectedCount: records?.totalRecords
              }}
            />
          </>
        ) : (
          <>
            {/* Do you want to select all rows across multiple pages ? */}
            <FormattedMessage id="superTable.enable.selectAll" />
          </>
        )}
      </p>

      {selectionMode !== SELECTION_MODE.ALL_SELECT ? (
        <Button onClick={onSelectAll} plain>
          <FormattedMessage id="superTable.selectAll.button" />
          {/* Yes select all records */}
        </Button>
      ) : (
        <Button onClick={clearSelection} plain>
          <FormattedMessage id="superTable.clearSelection.button" />
          {/* Clear selection */}
        </Button>
      )}
    </SelectAllBannerStyles>
  );
};

const defaultColumns = {
  minWidth: 30,
  maxWidth: 500,
  width: 150,
  Link: LinkCell,
  Text: TextCell,
  Action: ActionCell
};

const checkBoxSizeValues = {
  sm: 35,
  md: 50,
  lg: 70
};

function SuperTable(props) {
  const {
    data,
    refreshTable,
    context,
    rowCount,
    setRowCount,
    columnWidthPersonalization,
    user,
    saveColumnWidth,
    tableName,
    dynamicRowCount,
    setPageSize,
    defaultColumnCells,
    noBulkSelect,
    maintainPageOnRecordsChange,
    disableSelectAll,
    isClientPagination,
    saveTableRowCount,
    emptyState,
    actions,
    checkDisabledRow,
    alignStart,
    checkRowHighlight,
    padded = null,
    checkBoxSize = 'sm',
    checkBoxDisabledCheck,
    sortingPersonalization = false,
    saveSortingPersonalization,
    viewId = 'default',
    maintainSelection,
    idKey,
    enableSelectAll,
    selectAllBannerStyles,
    alignEmptyState = false,
    noOverscan,
    overScanCount,
    tableInitialState = null,
    noHeaderBorder = false,
    headerBgColor,
    enableColumnPinning,
    defaultShowFilter = false,
    groupAccessor,
    isGroupingEnabled = false,
    wsPayload,
    enableLiveUpdate,
    liveUpdateCondition,
    showLiveUpdate,
    isLiveUpdateInProgress,
    onMessage,
    onLiveUpdateRefresh,
    registerPageDelay,
    defaultExpanded = false,
    isSuperList,
    showInlineFilter,
    containerClassName = '',
    headerSticky = false,
    stickyTop = 0
  } = props;

  const broadcastChannelRef = useRef(null);
  if (enableLiveUpdate && !broadcastChannelRef?.current) {
    broadcastChannelRef.current = new BroadcastChannel('SuperShare');
  }

  const sendWSEvent = ({ event, payloadData }) => {
    if (!isEmpty(payloadData) && broadcastChannelRef.current) {
      const postMessageData = {
        context: 'pageUpdate',
        payload: {
          type: 'CLIENT_EVENT',
          event: event,
          epochMillis: new Date().getTime().toString(),
          ...payloadData
        }
      };

      broadcastChannelRef.current.postMessage(
        JSON.stringify({
          name: 'wsdata',
          data: postMessageData
        })
      );
    }
  };

  const allColumns = React.useMemo(() => {
    return { ...defaultColumns, ...defaultColumnCells };
  }, [defaultColumnCells, data, props.listInfo]);

  const columnFromProps = useRef(null);
  columnFromProps.current = props.columns;

  const dataByRef = useRef(null);
  dataByRef.current = data;

  const contextByRef = useRef(null);
  contextByRef.current = context;

  const [rowSelection, setRowSelection] = useState(maintainSelection ? [] : {});
  const [showBanner, setShowBanner] = useState(false);

  const [selectionMode, setSelectionMode] = useState(null);
  const selectionModeRef = useRef(null);
  selectionModeRef.current = selectionMode;

  const listInfoRef = useRef(null);
  listInfoRef.current = props.listInfo;

  // FUNCTIONS TO MAINTAIN RECORDS ON PAGE CHANGE

  // ROW SELECTION FUNCTIONS
  const onSelectionChange = (e) => {
    const dataId = e.target.value;
    const currentIndex = data.findIndex((el) => el?.[idKey] === dataId);
    let currentData = null;
    if (currentIndex > -1) {
      currentData = data?.[currentIndex];
    }

    if (
      selectionModeRef.current &&
      selectionModeRef.current === SELECTION_MODE.ALL_SELECT
    ) {
      const unSelectedRecords =
        contextByRef.current.records?.unSelectedRecords || [];
      const selectedIndex = unSelectedRecords.findIndex(
        (el) => el?.[idKey] === dataId
      );

      if (currentData && selectedIndex > -1) {
        unSelectedRecords.splice(selectedIndex, 1);
      } else {
        unSelectedRecords.push(currentData);
      }
      const totalRecords =
        listInfoRef.current.totalCount - unSelectedRecords.length;
      const allSelected = unSelectedRecords.length === 0;
      const selectionData = {
        allSelected: allSelected,
        seletedRecords: null,
        unSelectedRecords: unSelectedRecords,
        totalRecords: totalRecords,
        filterCondition: listInfoRef.current.condition || null
      };
      context.setRecords({ ...selectionData });
      setRowSelection({ ...selectionData });
    } else if (enableSelectAll) {
      const selectedRecords =
        contextByRef.current.records?.selectedRecords || [];
      const selectedIndex = selectedRecords.findIndex(
        (el) => el?.[idKey] === dataId
      );

      if (currentData && selectedIndex > -1) {
        selectedRecords.splice(selectedIndex, 1);
      } else {
        selectedRecords.push(currentData);
      }
      const totalRecords = selectedRecords.length;
      const selectionData = {
        allSelected: false,
        selectedRecords: selectedRecords,
        unSelectedRecords: null,
        totalRecords: totalRecords,
        filterCondition: listInfoRef.current.condition || null
      };
      context.setRecords({ ...selectionData });
      setRowSelection({ ...selectionData });
    } else {
      const records =
        contextByRef.current.records && contextByRef.current.records?.length > 0
          ? cloneDeep(contextByRef.current.records)
          : [];
      const selectedIndex = records.findIndex((el) => el?.[idKey] === dataId);
      if (currentData && selectedIndex > -1) {
        records.splice(selectedIndex, 1);
      } else {
        records.push(currentData);
      }
      if (context?.setRecords) {
        setRowSelection([...records]);
        context.setRecords([...records]);
      }
    }
  };

  const handleMultipleSelections = (subRows, isChecked) => {
    if (!Array.isArray(subRows) || !subRows?.length) {
      return;
    }

    const dataIds = subRows.map((r) => r[idKey]);
    // Get current data objects for valid IDs
    const dataToProcess = dataIds
      .map((dataId) => {
        const currentIndex = data.findIndex((el) => el?.[idKey] === dataId);
        return currentIndex > -1 ? data[currentIndex] : null;
      })
      .filter(Boolean); // Remove null items

    // Handle ALL_SELECT mode
    if (
      selectionModeRef.current &&
      selectionModeRef.current === SELECTION_MODE.ALL_SELECT
    ) {
      let unSelectedRecords =
        contextByRef.current.records?.unSelectedRecords || [];

      if (isChecked) {
        // Remove these items from unselected (making them selected)
        unSelectedRecords = unSelectedRecords.filter(
          (record) =>
            !dataToProcess.some((item) => item[idKey] === record[idKey])
        );
      } else {
        // Add these items to unselected (making them unselected)
        const existingUnselectedIds = new Set(
          unSelectedRecords.map((item) => item?.[idKey])
        );

        // Add only items not already in unselected list
        dataToProcess.forEach((item) => {
          if (!existingUnselectedIds.has(item[idKey])) {
            unSelectedRecords.push(item);
          }
        });
      }

      const totalRecords =
        listInfoRef.current.totalCount - unSelectedRecords.length;
      const allSelected = unSelectedRecords.length === 0;

      const selectionData = {
        allSelected: allSelected,
        seletedRecords: null,
        unSelectedRecords: unSelectedRecords,
        totalRecords: totalRecords,
        filterCondition: listInfoRef.current.condition || null
      };

      context.setRecords({ ...selectionData });
      setRowSelection({ ...selectionData });
    }
    // Handle enableSelectAll mode
    else if (enableSelectAll) {
      let selectedRecords = contextByRef.current.records?.selectedRecords || [];

      if (isChecked) {
        // Add these items to selected
        const existingSelectedIds = new Set(
          selectedRecords.map((item) => item?.[idKey])
        );

        // Add only items not already in selected list
        dataToProcess.forEach((item) => {
          if (!existingSelectedIds.has(item[idKey])) {
            selectedRecords.push(item);
          }
        });
      } else {
        // Remove these items from selected
        selectedRecords = selectedRecords.filter(
          (record) =>
            !dataToProcess.some((item) => item[idKey] === record[idKey])
        );
      }

      const totalRecords = selectedRecords.length;

      const selectionData = {
        allSelected: false,
        selectedRecords: selectedRecords,
        unSelectedRecords: null,
        totalRecords: totalRecords,
        filterCondition: listInfoRef.current.condition || null
      };

      context.setRecords({ ...selectionData });
      setRowSelection({ ...selectionData });
    }
    // Handle default mode
    else {
      let records =
        contextByRef.current.records && contextByRef.current.records?.length > 0
          ? cloneDeep(contextByRef.current.records)
          : [];

      if (isChecked) {
        // Add these items to records
        const existingRecordIds = new Set(records.map((item) => item?.[idKey]));

        // Add only items not already in records list
        dataToProcess.forEach((item) => {
          if (!existingRecordIds.has(item[idKey])) {
            records.push(item);
          }
        });
      } else {
        // Remove these items from records
        records = records.filter(
          (record) =>
            !dataToProcess.some((item) => item[idKey] === record[idKey])
        );
      }

      if (context?.setRecords) {
        setRowSelection([...records]);
        context.setRecords([...records]);
      }
    }
  };

  const isRowSelected = (row) => {
    if (selectionModeRef.current === SELECTION_MODE.ALL_SELECT) {
      const data = row.original;
      const records = context.records?.unSelectedRecords || [];
      const isSelected =
        records.findIndex((el) => el?.[idKey] === data?.[idKey]) === -1;
      return isSelected;
    } else if (enableSelectAll) {
      const data = row.original;
      const records = context.records?.selectedRecords || [];
      const isSelected =
        records.findIndex((el) => el?.[idKey] === data?.[idKey]) > -1;
      return isSelected;
    } else {
      const data = row.original;
      const records =
        contextByRef.current.records && contextByRef.current.records?.length > 0
          ? contextByRef.current.records
          : [];
      const isSelected =
        records.findIndex((el) => el?.[idKey] === data?.[idKey]) > -1;
      return isSelected;
    }
  };

  const isRowHeaderSelected = (row) => {
    if (!row) return false;

    // Different logic based on selection mode
    if (selectionModeRef.current === SELECTION_MODE.ALL_SELECT) {
      if (isHeaderIndeterminate(row)) return false;
      const unSelectedRecords =
        contextByRef.current.records?.unSelectedRecords || [];
      const unselectedMap = new Set(unSelectedRecords.map((r) => r[idKey]));
      return !row.original.subRows?.every((r) => unselectedMap.has(r[idKey]));
    } else if (enableSelectAll) {
      const selectedRecords =
        contextByRef.current.records?.selectedRecords || [];
      const selectedMap = new Set(selectedRecords.map((r) => r[idKey]));
      return row.original.subRows?.every((r) => selectedMap.has(r[idKey]));
    } else {
      // In standard mode, we check if the row is in the records array
      const records = contextByRef.current?.records || [];
      const recordsMap = new Set(records.map((r) => r[idKey]));
      return row.original.subRows?.every((r) => recordsMap.has(r[idKey]));
    }
  };

  // PAGE SELECT FUNCTION
  const selectPageRows = (e) => {
    const isChecked = e.target.checked;
    const pageRecords = dataByRef.current;
    let records = context?.records ? cloneDeep(context?.records) : [];
    if (selectionModeRef.current === SELECTION_MODE.ALL_SELECT) {
      records = contextByRef.current?.records?.unSelectedRecords || [];
    } else if (enableSelectAll) {
      records = contextByRef.current?.records?.selectedRecords || [];
    }

    if (isChecked) {
      if (enableSelectAll) {
        setShowBanner(true);
      }
      if (selectionModeRef.current === SELECTION_MODE.ALL_SELECT) {
        pageRecords.forEach((el) => {
          const selectedIndex = records.findIndex(
            (rec) => rec?.[idKey] === el?.[idKey]
          );
          if (selectedIndex > -1) {
            records.splice(selectedIndex, 1);
          }
        });
      } else {
        pageRecords.forEach((el) => {
          const isAlreadySelected =
            records.findIndex((rec) => rec?.[idKey] === el?.[idKey]) > -1;
          if (!isAlreadySelected) {
            records.push(el);
          }
        });
      }
    } else {
      if (selectionModeRef.current === SELECTION_MODE.ALL_SELECT) {
        pageRecords.forEach((el) => {
          const isSelected =
            records.findIndex((rec) => rec?.[idKey] === el?.[idKey]) > -1;
          if (!isSelected) {
            records.push(el);
          }
        });
      } else {
        pageRecords.forEach((el) => {
          const selectedIndex = records.findIndex(
            (rec) => rec?.[idKey] === el?.[idKey]
          );
          if (selectedIndex > -1) {
            records.splice(selectedIndex, 1);
          }
          if (records.length === 0) {
            setShowBanner(false);
          }
        });
      }
    }

    if (context?.setRecords) {
      if (selectionModeRef.current === SELECTION_MODE.ALL_SELECT) {
        const totalRecords = listInfoRef.current.totalCount - records.length;
        const allSelected = !records?.length;
        //  If records are present it'll be unselected
        //  So, allSelected will be false
        const selectionData = {
          allSelected: allSelected,
          selectedRecords: null,
          unSelectedRecords: records,
          totalRecords: totalRecords,
          filterCondition: listInfoRef.current.condition || null
        };
        setRowSelection({ ...selectionData });
        context.setRecords({ ...selectionData });
      } else if (enableSelectAll) {
        const totalRecords = records.length;
        const selectionData = {
          allSelected: false,
          selectedRecords: records,
          unSelectedRecords: null,
          totalRecords: totalRecords,
          filterCondition: listInfoRef.current.condition || null
        };
        setRowSelection({ ...selectionData });
        context.setRecords({ ...selectionData });
      } else {
        setRowSelection([...records]);
        context.setRecords([...records]);
      }
    }
  };

  const isPageSelected = () => {
    const pageRecords = dataByRef.current || [];
    const validationArray = [];

    if (selectionModeRef.current === SELECTION_MODE.ALL_SELECT) {
      const records = contextByRef.current?.records?.unSelectedRecords || [];
      pageRecords.forEach((el) => {
        const isAlreadySelected =
          records.findIndex((rec) => rec?.[idKey] === el?.[idKey]) === -1;
        if (!isAlreadySelected) {
          validationArray.push(el);
        }
      });

      return pageRecords.length && validationArray.length === 0;
    }
    if (enableSelectAll) {
      const records = contextByRef.current?.records?.selectedRecords || [];
      pageRecords.forEach((el) => {
        const isAlreadySelected =
          records.findIndex((rec) => rec?.[idKey] === el?.[idKey]) > -1;
        if (!isAlreadySelected) {
          validationArray.push(el);
        }
      });
      return pageRecords.length && validationArray.length === 0;
    } else {
      const records =
        contextByRef.current.records && contextByRef.current.records?.length > 0
          ? contextByRef.current.records
          : [];
      pageRecords.forEach((el) => {
        const isAlreadySelected =
          records.findIndex((rec) => rec?.[idKey] === el?.[idKey]) > -1;
        if (!isAlreadySelected) {
          validationArray.push(el);
        }
      });
      return pageRecords.length && validationArray.length === 0;
    }
  };
  // END : FUNCTIONS TO MAINTAIN RECORDS ON PAGE CHANGE

  // FUNCTION ON PAGE SELECT ALL
  const onSelectAll = () => {
    setSelectionMode(SELECTION_MODE.ALL_SELECT);
    if (context.setRecords) {
      const selectionData = {
        allSelected: true,
        selectedRecords: null,
        unSelectedRecords: null,
        totalRecords: listInfoRef.current.totalCount,
        filterCondition: listInfoRef.current.condition || null
      };
      context.setRecords(selectionData);
      setRowSelection(selectionData);
    }
  };

  const clearSelection = () => {
    setSelectionMode(null);
    setShowBanner(false);
    if (context.setRecords) {
      setRowSelection([]);
      context.setRecords([]);
    }
  };

  const hasSelected = useCallback((base, arr) => {
    // returns true if any  item present in array 1 is present in array 2
    const isPresent = base.some((r) => {
      return arr.includes(r);
    });
    return isPresent;
  }, []);

  const checkIsIndeterminate = useCallback(() => {
    const pageRecords = dataByRef.current || [];
    let indeterminate = false;
    if (enableSelectAll && selectionMode === SELECTION_MODE.ALL_SELECT) {
      const unSelectedRecords = context?.records?.unSelectedRecords || [];
      if (unSelectedRecords.length === pageRecords.length) {
        indeterminate = false; // If all records in the page is unselected
      } else {
        indeterminate = hasSelected(pageRecords, unSelectedRecords);
      }
    } else if (enableSelectAll) {
      const selectedRecords = context?.records?.selectedRecords || [];
      indeterminate = hasSelected(pageRecords, selectedRecords);
    } else {
      const selectedRecords = context?.records || [];
      indeterminate = hasSelected(pageRecords, selectedRecords);
    }
    return indeterminate;
  }, [dataByRef.current, selectionMode, enableSelectAll, context]);

  // END: FUNCTION ON SELECT ALL

  const bulkSelectRow = [
    {
      id: 'select',
      size: checkBoxSizeValues[checkBoxSize],
      minSize: checkBoxSizeValues[checkBoxSize],
      maxSize: checkBoxSizeValues[checkBoxSize],
      disableResizing: true,
      disableSortBy: true,
      disableFilters: true,
      enablePin: enableColumnPinning && true,
      disableUnpin: enableColumnPinning && true,
      defaultPin: enableColumnPinning && 'left',
      header: ({ table }) => (
        <>
          {disableSelectAll ? (
            <> </>
          ) : (
            <>
              <IndeterminateCheckbox
                className={'ml-0'}
                {...{
                  checked: !maintainSelection
                    ? table.getIsAllRowsSelected()
                    : isPageSelected(),
                  indeterminate: !maintainSelection
                    ? table.getIsSomeRowsSelected()
                    : checkIsIndeterminate(),
                  onChange: !maintainSelection
                    ? table.getToggleAllRowsSelectedHandler()
                    : selectPageRows
                }}
              />
            </>
          )}
        </>
      ),
      cell: ({ row }) => (
        <div className={!padded ? 'ml-5' : ''}>
          <IndeterminateCheckbox
            className={'ml-0'}
            {...{
              checked: !maintainSelection
                ? row.getIsSelected()
                : isRowSelected(row),
              indeterminate: row.getIsSomeSelected(),
              onChange: !maintainSelection
                ? row.getToggleSelectedHandler()
                : onSelectionChange,
              row: row,
              checkBoxDisabledCheck: checkBoxDisabledCheck,
              superIndex: 'check'
            }}
            idKey={idKey}
          />
        </div>
      )
    }
  ];

  const resetSelection = useCallback(() => {
    setRowSelection({});
  });

  // Prepare grouped data
  const prepareGroupedData = (rawData) => {
    const groups = {};
    rawData.forEach((item) => {
      const accessor = groupAccessor(item);
      const groupKey = accessor.id;
      const groupName = accessor.name ?? accessor.empty;
      const additionalHeaderProperties =
        accessor?.additionalHeaderProperties ?? []; // to store additional properties for the group header
      if (!groups[groupKey]) {
        groups[groupKey] = {
          id: groupKey,
          isGroupHeader: true, // Flag to identify group headers
          groupName,
          showAdditionalHeaderProperties: !!additionalHeaderProperties?.length,
          additionalHeaderProperties,
          subRows: [] // Will store references to child rows
        };
      }
      // Add reference to this item in the group's children array
      groups[groupKey].subRows.push(item);
    });

    return Object.values(groups);

    // const result = [];

    // // For each group, add the group header followed by its items
    // Object.values(groups).forEach((group) => {
    //   result.push(group); // Add the group header
    //   group.children.forEach((item) => {
    //     // Optionally add a reference to the parent group in each item
    //     item.parentGroupId = group.id;
    //     result.push(item);
    //   });
    // });

    // return result;
  };

  const handleSelectionChange = (updatedSelection) => {
    if (isSuperList) {
      const newSelection = updatedSelection(rowSelection);
      setRowSelection(newSelection);
      if (context?.setRecords) {
        const selected = Object.keys(newSelection)
          .filter((id) => newSelection[id])
          .map((id) => table.getRow(id).original);
        context.setRecords(selected);
      }
    } else {
      setRowSelection(updatedSelection);
    }
  };

  const refreshSelection = (updatedSelection) => {
    if (!maintainSelection) {
      const isSelectionEmpty = isEmpty(updatedSelection);
      if (isSelectionEmpty) {
        setRowSelection({});
      } else {
        const selectedData = {};
        updatedSelection.forEach((record) => {
          const index = data.findIndex((r) => isEqual(r, record));
          if (index > -1) {
            selectedData[index] = true;
          }
        });
        setRowSelection(selectedData);
      }
    }
  };

  const processedData = useMemo(() => {
    return isGroupingEnabled ? prepareGroupedData(data) : data;
  }, [data, isGroupingEnabled]);

  const table = useReactTable({
    data: processedData,
    columns: !noBulkSelect
      ? [...bulkSelectRow, ...columnFromProps.current]
      : [...columnFromProps.current],
    getRowId: (row, index, parent) => {
      // Create unique IDs for each row
      // For group headers, use their own ID
      // For children, create a nested ID based on parent
      if (row.isGroupHeader) {
        return row.id;
      }

      // For children rows, use parent ID + index
      return row[idKey];
    },
    getRowCanExpand: (row) => row.original && row.original.isGroupHeader,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    debugTable: true,
    columnResizeMode: 'onChange',
    manualPagination: true,
    initialState: {
      ...(tableInitialState ? { ...tableInitialState } : null),
      expanded: !!defaultExpanded
    },
    state: {
      rowSelection
    },
    onRowSelectionChange: !maintainSelection ? handleSelectionChange : () => {},
    manualFiltering: true
  });

  const columnPinningState =
    enableColumnPinning && table.getState().columnPinning;

  // SORT FUNCTIONS
  const sortedCol = listInfoRef.current?.sort?.[0]?.attribute || null;
  const sortOrder = listInfoRef.current?.sort?.[0]?.order || null;

  const pages = React.useMemo(() => {
    return (
      Math.ceil(
        listInfoRef.current?.totalCount / listInfoRef.current?.pageSize
      ) || 0
    );
  }, [listInfoRef.current]);

  const pagination = React.useMemo(() => {
    return (
      Math.ceil(
        listInfoRef.current?.totalCount / listInfoRef.current?.pageSize
      ) > 1
    );
  }, [listInfoRef.current]);

  const getSort = useCallback((attributeName) => {
    if (sortedCol === attributeName) {
      switch (sortOrder) {
        case 'ASC':
          return [
            {
              attribute: attributeName,
              order: 'DESC'
            }
          ];
        case 'DESC':
        default:
          return [
            {
              attribute: attributeName,
              order: 'ASC'
            }
          ];
      }
    } else {
      return [
        {
          attribute: attributeName,
          order: 'ASC'
        }
      ];
    }
  });

  const sortCurrentColumn = useCallback((column) => {
    if (column.columnDef.disableSortBy) return;
    const sortingAttribute = props.getAttributeName
      ? props.getAttributeName(column?.columnDef?.field || {})
      : getAttributeName(column?.columnDef?.field);
    const sortingData = getSort(sortingAttribute);
    refreshTable(context.personalization, {
      pageSize: rowCount,
      page: 1,
      // condition: context.viewConditions || null,
      sort: sortingData
    });
    if (sortingPersonalization) {
      saveSortingPersonalization(tableName, user, sortingData, viewId);
    }
  });
  // END SORT FUNCTIONS

  // PAGINATION FUNCTIONS
  const recordCount = React.useMemo(() => {
    return listInfoRef?.current?.totalCount;
  }, [listInfoRef.current]);

  const activePage = React.useMemo(() => {
    return listInfoRef?.current?.page || 0;
  }, [listInfoRef.current]);

  const navigatePages = useCallback(
    (pageNumber) => {
      if (!maintainSelection) {
        resetSelection();
      }
      refreshTable(context.personalization, {
        pageSize: rowCount,
        page: pageNumber || 0,
        // condition: context.viewConditions || null,
        sort: listInfoRef.current?.sort || null
      });
    },
    [rowCount, listInfoRef.current]
  );

  // END PAGINATION FUNCTIONS

  // BULK SELECT FUNCTIONS

  useEffect(() => {
    // This useEffect need not run on first render
    if (!isSuperList) {
      if (!maintainSelection) {
        const selectedRows = table.getSelectedRowModel().flatRows;
        const rows = [];
        for (let i = 0; i < selectedRows.length; i++) {
          rows.push(selectedRows[i].original);
        }
        if (context.setRecords) {
          context.setRecords(rows);
        }
      }
    }
  }, [rowSelection]);

  useEffect(() => {
    if (!isSuperList) {
      if (!maintainSelection) {
        const selectedRows = table.getSelectedRowModel().flatRows;
        if (context?.records?.length !== selectedRows.length) {
          if (maintainPageOnRecordsChange) {
            refreshTable(context.personalization, {
              pageSize: rowCount,
              page: listInfoRef.current.page || 0,
              condition: context.viewConditions || null,
              sort: listInfoRef.current.sort || null
            });
          } else {
            refreshTable(context.personalization);
          }

          if (!context?.records?.length) resetSelection();
        }
      } else {
        if (enableSelectAll) {
          //  Clear selection mode and hide banner when no records are selected
          if (!context?.records?.totalRecords) {
            setShowBanner(false);
            setSelectionMode(null);
          }
        }
        // rowSelection is local state inside table where selected records are maintained
        const recordsLength = context?.records?.length;
        if (recordsLength !== rowSelection.length) {
          //  Refresh table (Make API call when context is reset externally)
          if (maintainPageOnRecordsChange) {
            refreshTable(context.personalization, {
              pageSize: rowCount,
              page: listInfoRef.current.page || 0,
              condition: context.viewConditions || null,
              sort: listInfoRef.current.sort || null
            });
          } else {
            refreshTable(context.personalization);
          }
        }
      }
    }
    if (isSuperList) {
      if (enableSelectAll) {
        //  Clear selection mode and hide banner when no records are selected
        if (!context?.records?.totalRecords) {
          setShowBanner(false);
          setSelectionMode(null);
        }
      }
    }
  }, [context?.records]);
  // END BULK SELECT FUNCTIONS

  const eventToSend = useRef('REGISTER_PAGE');
  const isPageRegisteredRef = React.useRef(false);
  let registerEventTimeOutId = null;

  // Whenever "liveUpdateCondition" changes we'll call liveUpdateCondition
  useEffect(() => {
    if (
      enableLiveUpdate &&
      !isEmpty(wsPayload) &&
      eventToSend.current === 'LIST_VIEW_UPDATE'
    ) {
      sendWSEvent({ event: 'LIST_VIEW_UPDATE', payloadData: wsPayload });
    }
  }, [liveUpdateCondition, wsPayload?.data?.condition]);

  // UseEffect will be called on initialisation and connect to websocket

  const handleBeforeUnload = () => {
    sendWSEvent({ event: 'UNREGISTER_PAGE', payloadData: wsPayload });
    loggerInfo('UNREGISTER_LIST_ON_UNLOAD', {
      payload: wsPayload
    });
  };

  useEffect(() => {
    if (enableLiveUpdate && !isEmpty(wsPayload)) {
      if (registerEventTimeOutId) {
        clearTimeout(registerEventTimeOutId);
      }
      if (eventToSend.current === 'REGISTER_PAGE') {
        if (registerPageDelay) {
          registerEventTimeOutId = setTimeout(() => {
            sendWSEvent({ event: eventToSend.current, payloadData: wsPayload });
            eventToSend.current = 'LIST_VIEW_UPDATE';
            isPageRegisteredRef.current = true;
          }, registerPageDelay);
        } else {
          sendWSEvent({ event: eventToSend.current, payloadData: wsPayload });
          eventToSend.current = 'LIST_VIEW_UPDATE';
          isPageRegisteredRef.current = true;
        }
      }
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    // Whenever the context is changed from the superList this event will be emitted
    EventEmitter.subscribe('refreshSelection', refreshSelection);
    return function cleanup() {
      EventEmitter.unsubscribe('refreshSelection');
      if (
        enableLiveUpdate &&
        !isEmpty(wsPayload) &&
        isPageRegisteredRef.current
      ) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        sendWSEvent({ event: 'UNREGISTER_PAGE', payloadData: wsPayload });
        eventToSend.current = 'REGISTER_PAGE';
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.close();
        }
      }
    };
  }, []);

  const onRefresh = () => {
    if (onLiveUpdateRefresh) {
      onLiveUpdateRefresh(context.personalization, {
        pageSize: rowCount,
        page: listInfoRef.current.page || 0,
        condition: context.viewConditions || null,
        sort: listInfoRef.current.sort || null
      });
    } else {
      refreshTable(context.personalization, {
        pageSize: rowCount,
        page: listInfoRef.current.page || 0,
        condition: context.viewConditions || null,
        sort: listInfoRef.current.sort || null
      });
    }
  };

  // When there is an event from WS this function will pass it to parent
  if (enableLiveUpdate) {
    broadcastChannelRef.current.onmessage = (event) => {
      if (onMessage) {
        if (event?.data) {
          const eventData = JSON.parse(event?.data);
          onMessage(eventData);
        }
      }
    };
  }

  // Filter Functions
  const [showFilters, setShowFilters] = useState(
    !!context.viewConditions || defaultShowFilter
  );

  useEffect(() => {
    // Added this useEffect to Toggle filter based on viewId in superList
    if (isSuperList) {
      setShowFilters(showInlineFilter);
    }
  }, [showInlineFilter, isSuperList]);

  const toggleFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  // End Filter Functions

  // rowCount Function
  const changePageSize = (size) => {
    setRowCount(size);
    saveTableRowCount(tableName, size, user);
    refreshTable(context.personalization, {
      pageSize: size,
      page: 1,
      // condition: context.viewConditions || null,
      sort: listInfoRef.current.sort || null
    });
  };

  const checkHighlight = (row) => {
    if (checkRowHighlight) {
      return checkRowHighlight(row);
    }
    return false;
  };

  // End rowCount function

  const tableContainerRef = useRef(null);

  const getOverScan = useCallback(() => {
    if (noOverscan && overScanCount) {
      return overScanCount;
    }
    if (noOverscan) {
      return data?.length > 0 ? data?.length : 50;
    }
    return 50; // High overscan to ensure all rows are rendered
  }, [data?.length, noOverscan, overScanCount]);

  const { rows } = table.getRowModel();
  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => 55,
    overscan: getOverScan(),
    scrollMargin: tableContainerRef.current?.offsetTop ?? 0
  });

  const totalSize = virtualizer?.getTotalSize() || 0;
  const virtualRows = virtualizer?.getVirtualItems() || [];
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const isRowDisabled = (row) => {
    if (checkDisabledRow) {
      return checkDisabledRow(row);
    }
    return false;
  };

  const onGroupHeaderSelectionChange = (e, row, idKey) => {
    const isChecked = e.target.checked;
    handleMultipleSelections(row.original.subRows, isChecked);
  };

  const isHeaderIndeterminate = (row) => {
    // If no children, header can't be indeterminate
    if (!row?.original?.subRows || row.original.subRows.length === 0) {
      return false;
    }

    const children = row.original.subRows;
    const childrenIds = new Set(children.map((child) => child[idKey]));
    let selectedCount = 0;

    // Handle ALL_SELECT mode
    if (
      selectionModeRef.current &&
      selectionModeRef.current === SELECTION_MODE.ALL_SELECT
    ) {
      const unSelectedRecords =
        contextByRef.current.records?.unSelectedRecords || [];
      const unselectedIds = new Set(
        unSelectedRecords.map((item) => item[idKey])
      );

      // Count how many children are selected (not in unselected list)
      children.forEach((child) => {
        if (!unselectedIds.has(child[idKey])) {
          selectedCount++;
        }
      });
    }
    // Handle enableSelectAll mode
    else if (enableSelectAll) {
      const selectedRecords =
        contextByRef.current.records?.selectedRecords || [];
      const selectedIds = new Set(selectedRecords.map((item) => item[idKey]));

      // Count how many children are in the selected list
      children.forEach((child) => {
        if (selectedIds.has(child[idKey])) {
          selectedCount++;
        }
      });
    }
    // Handle default mode
    else {
      const selectedRecords = contextByRef.current.records || [];
      const selectedIds = new Set(selectedRecords.map((item) => item[idKey]));

      // Count how many children are in the selected list
      children.forEach((child) => {
        if (selectedIds.has(child[idKey])) {
          selectedCount++;
        }
      });
    }

    // Indeterminate when some (but not all) children are selected
    return selectedCount > 0 && selectedCount < children.length;
  };

  return (
    <>
      {enableSelectAll && maintainSelection && pagination && showBanner && (
        <>
          <SelectAllBanner
            selectAllBannerStyles={selectAllBannerStyles}
            onSelectAll={onSelectAll}
            records={context?.records}
            clearSelection={clearSelection}
            selectionMode={selectionMode}
            totalCount={listInfoRef.current.totalCount}
          />
        </>
      )}

      {showLiveUpdate && (
        <RefreshButtonStyles
          className="refresh-notification fx actr g10"
          styles={props?.refreshButtonStyles}
        >
          {isLiveUpdateInProgress ? (
            <div className="flex g10 actr">
              <ApiProgressLoader isLoading />
              <FormattedMessage id="common.updating" />
            </div>
          ) : (
            <>
              <SVGIcon
                size="sm"
                color="var(--sops-white-to-white)"
                name="Lightning"
              />
              <FormattedMessage id="app.common.new.updates" />
              <div onClick={onRefresh} className="refresh pointer">
                <FormattedMessage id="app.common.refresh" />
              </div>
            </>
          )}
        </RefreshButtonStyles>
      )}
      <Styles
        className="p-2"
        fullHeight={!pagination && !dynamicRowCount}
        isLoading={props.isLoading && !data.length}
        alignStart={alignStart}
        padded={padded}
        showBanner={showBanner}
        headerSticky={headerSticky}
        stickyTop={stickyTop}
      >
        <div className="h-2" />
        <div
          ref={tableContainerRef}
          className={`superTable container position-relative ${containerClassName}`}
        >
          <table className="table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHeader
                        key={header.id}
                        header={header}
                        getAttributeName={props.getAttributeName}
                        getEntityAttributeName={props.getEntityAttributeName}
                        sortedCol={sortedCol}
                        sortOrder={sortOrder}
                        sortCurrentColumn={sortCurrentColumn}
                        columnWidthPersonalization={columnWidthPersonalization}
                        user={user}
                        saveColumnWidth={saveColumnWidth}
                        tableName={tableName}
                        context={context}
                        // listInfo={listInfoRef.current}
                        toggleFilters={toggleFilters}
                        showFilters={showFilters}
                        noHeaderBorder={noHeaderBorder}
                        headerBgColor={headerBgColor}
                      />
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="table-body">
              {/* {paddingTop > 0 && (
                <tr>
                  <td style={{ height: `${paddingTop}px` }} />
                </tr>
              )} */}
              {props.isLoading && !data.length && (
                <>
                  <LoadingState />
                </>
              )}
              {data.length > 0 && (
                <>
                  {virtualizer.getVirtualItems().map((virtualRow, idx) => {
                    const row = rows[virtualRow.index];
                    const isSelected = !maintainSelection
                      ? row.getIsSelected()
                      : isRowSelected(row);
                    const isDisabled = isRowDisabled(row);
                    const isHighlight = checkHighlight(row);
                    const rowData = data[virtualRow.index];
                    return (
                      <tr
                        key={`${row.id}-${idx}${
                          tableInitialState &&
                          tableInitialState.hasOwnProperty('expanded')
                            ? `-${row.getIsExpanded()}`
                            : ''
                        }`}
                        className={`table-content-rows ${
                          props.isLoading ? 'loading' : ''
                        } ${isSelected ? 'selected' : ''} ${
                          isDisabled ? 'disabled' : ''
                        } ${isHighlight ? 'highlight' : ''} `}
                      >
                        <DataCell
                          columnPinningState={columnPinningState}
                          data={rowData}
                          row={row}
                          defaultColumn={allColumns}
                          isSelected={isSelected}
                          isLoading={props.isLoading}
                          editCellId={props.editCellId}
                          actions={actions} // FIX: requestors table - Need to remove this and pass from columns
                          onGroupHeaderSelectionChange={
                            onGroupHeaderSelectionChange
                          }
                          maintainSelection={maintainSelection}
                          isRowSelected={isRowSelected}
                          checkBoxDisabledCheck={checkBoxDisabledCheck}
                          idKey={idKey}
                          isRowHeaderSelected={isRowHeaderSelected}
                          isHeaderIndeterminate={isHeaderIndeterminate}
                        />
                      </tr>
                    );
                  })}
                </>
              )}

              {!props.isLoading && !data.length && (
                <div
                  className={`flex ch-50 center jcenter mt-50 ${
                    alignEmptyState ? '' : 'noDataCard'
                  }`}
                >
                  <NoDataCard
                    imageURL={`url(${emptyState?.nodataImage ||
                      `${envConfig.imageRoot}svg/not-found.svg`})`}
                    text={
                      emptyState?.heading || (
                        <FormattedMessage id="app.selectionsheet.emptyState" />
                      )
                    }
                    description={emptyState?.description}
                  />
                </div>
              )}

              {/* {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: `${paddingBottom}px` }} />
                </tr>
              )} */}
            </tbody>
          </table>
        </div>
        <div className={`mt10 ${padded && paddedValuesForPagination[padded]}`}>
          {dynamicRowCount ? (
            <>
              <Pagination
                onPageChange={navigatePages}
                setPageSize={setPageSize}
                rowCount={rowCount}
                recordCount={recordCount}
                pages={pages}
                page={activePage}
                setRowCount={changePageSize}
                dynamicRowCount={dynamicRowCount}
                isClientPagination={isClientPagination}
              />
            </>
          ) : (
            <>
              {pagination && (
                <Pagination
                  onPageChange={navigatePages}
                  setPageSize={setPageSize}
                  rowCount={rowCount}
                  recordCount={recordCount}
                  pages={pages}
                  page={activePage}
                  setRowCount={changePageSize}
                  dynamicRowCount={dynamicRowCount}
                  isClientPagination={isClientPagination}
                />
              )}
            </>
          )}
        </div>
      </Styles>
    </>
  );
}

export default React.memo(withErrorBoundary(SuperTable));
